/**
 * ศึกกิลด์ (data/guild.ts `GUILD_WAR`): every day 20:00–20:30 in สมรภูมิธงสามผืน. Sign up until 19:50 (a reminder at
 * 19:45), choosing `npc` (against 1–2 AI guilds of simulated members) or `pvp` (other players' guilds, matched by a
 * server; with none, it falls back to `npc`). Banners are taken by standing beside them alone; a side scores every
 * second per banner held and per defeat; the defeated wait 10 s at their camp and lose nothing.
 *
 * Two parts:
 * - `GuildWarMatch`: one war, pure (sides, fighters, banners, points; randomness injected). A server runs the same
 *   class, placing its real players with `place` and reporting their defeats with `defeat`.
 * - `GuildWarSystem`: the clock (the player's local clock once the client sets it with `guildWarClock`, else the
 *   game's hour), the sign-up, the reminder, starting and ending the war, and the rewards. In this build the player's
 *   own fighter is simulated with the rest until the battlefield instance exists (round 3).
 */
import { rankFor } from '../cultivation';
import {
  GUILD_FIELD,
  GUILD_WAR,
  GUILD_WAR_MODES,
  GUILD_WAR_REWARDS,
  guildWarPhase,
  isGuildWarMode,
  SIM_GUILDS,
  type GuildWarMode,
  type GuildWarPhase,
} from '../data/guild';
import { addGuildContribution, guildLevel, type GuildState } from './guild';
import { GuildField, type GuildFieldState } from './guildField';
import type { SystemHost, WorldSystem } from './types';

// ───────────────────────── the match ─────────────────────────

export interface WarFighterDef {
  name: string;
  power: number;
  /** The player (in this build) or a real player (on a server). */
  player?: boolean;
}

export interface WarSideDef {
  /** A guild id (`own` for the player's founded guild, a simulated guild's id, or a server's). */
  guildId: string;
  name: string;
  /** Real players' guild (pvp) or simulated (npc). */
  human: boolean;
  /** The guild's emblem colour (its banners and camp on the field). */
  color?: string;
  fighters: WarFighterDef[];
}

export interface WarFighter extends WarFighterDef {
  side: number;
  x: number;
  y: number;
  alive: boolean;
  /** Match time it stands up again at its camp. */
  respawnAt: number;
  defeats: number;
  /** A simulated fighter's strength left (0–1): worn by duels it wins, regained at its camp. */
  hp: number;
  /** Moved by its own controller (a real player), not by the match's AI. */
  driven: boolean;
}

export interface WarBanner {
  id: string;
  holder: number | null;
  /** The side standing alone beside it, and for how long. */
  capSide: number | null;
  capT: number;
}

export type WarHappening =
  | { kind: 'capture'; banner: string; side: number }
  | { kind: 'defeat'; killer: number; victim: number };

export interface WarResult {
  winner: number;
  points: number[];
}

const dist2 = (a: { x: number; y: number }, b: { x: number; y: number }) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

export class GuildWarMatch {
  readonly fighters: WarFighter[] = [];
  readonly banners: WarBanner[];
  readonly points: number[];
  t = 0;

  constructor(
    readonly sides: readonly WarSideDef[],
    private readonly random: () => number,
  ) {
    if (sides.length < 2 || sides.length > GUILD_FIELD.camps.length) throw new Error('a guild war has 2–3 sides');
    this.points = sides.map(() => 0);
    this.banners = GUILD_FIELD.banners.map((b) => ({ id: b.id, holder: null, capSide: null, capT: 0 }));
    sides.forEach((s, side) => {
      const camp = GUILD_FIELD.camps[side]!;
      for (const f of s.fighters.slice(0, GUILD_WAR.maxPerSide))
        this.fighters.push({ ...f, side, x: camp.x, y: camp.y, alive: true, respawnAt: 0, defeats: 0, hp: 1, driven: false });
    });
  }

  /** Puts a driven fighter (a real player) at a point of the field. */
  place(i: number, x: number, y: number): void {
    const f = this.fighters[i]!;
    f.driven = true;
    f.x = x;
    f.y = y;
  }

  /** A defeat: the victim goes back to its camp for `GUILD_WAR.respawn` s; the killer's side scores. */
  defeat(killer: number, victim: number, out: WarHappening[] = []): void {
    const v = this.fighters[victim]!;
    if (!v.alive) return;
    v.alive = false;
    v.respawnAt = this.t + GUILD_WAR.respawn;
    const k = this.fighters[killer]!;
    k.defeats++;
    this.points[k.side]! += GUILD_WAR.pointsPerDefeat;
    out.push({ kind: 'defeat', killer, victim });
  }

  /** One step of the war: respawns, the AI's moves, duels, banners, points. */
  tick(dt: number): WarHappening[] {
    const out: WarHappening[] = [];
    this.t += dt;
    for (const f of this.fighters)
      if (!f.alive && this.t >= f.respawnAt) {
        const camp = GUILD_FIELD.camps[f.side]!;
        f.alive = true;
        f.hp = 1;
        f.x = camp.x;
        f.y = camp.y;
      }
    for (const f of this.fighters) if (f.alive && !f.driven) this.think(f, dt);
    this.duels(dt, out);
    this.capture(dt, out);
    for (const b of this.banners) if (b.holder !== null) this.points[b.holder]! += GUILD_WAR.pointsPerSecond * dt;
    return out;
  }

  /** Who is ahead: most points, then most banners held, then the lower side. */
  result(): WarResult {
    const held = (s: number) => this.banners.filter((b) => b.holder === s).length;
    let winner = 0;
    for (let s = 1; s < this.sides.length; s++) {
      const a = Math.floor(this.points[s]!);
      const w = Math.floor(this.points[winner]!);
      if (a > w || (a === w && held(s) > held(winner))) winner = s;
    }
    return { winner, points: this.points.map((p) => Math.floor(p)) };
  }

  private nearestEnemy(f: WarFighter, range: number): WarFighter | null {
    let best: WarFighter | null = null;
    let bd = range * range;
    for (const o of this.fighters)
      if (o.alive && o.side !== f.side) {
        const d = dist2(f, o);
        if (d <= bd) {
          bd = d;
          best = o;
        }
      }
    return best;
  }

  /**
   * A simulated fighter: worn down, back to its camp to recover; else at a foe close by, else to the nearest banner
   * its side does not hold, else to guard one.
   */
  private think(f: WarFighter, dt: number): void {
    const camp = GUILD_FIELD.camps[f.side]!;
    const atCamp = dist2(f, camp) <= GUILD_WAR.campReach ** 2;
    if (atCamp) f.hp = Math.min(1, f.hp + GUILD_WAR.campHeal * dt);
    const retreat = f.hp < GUILD_WAR.retreatHp || (atCamp && f.hp < 0.9);
    let goal: { x: number; y: number } | null = retreat ? camp : this.nearestEnemy(f, 8);
    let stop = retreat ? GUILD_WAR.campReach * 0.5 : GUILD_WAR.fightRange * 0.6;
    if (!goal) {
      const field = GUILD_FIELD.banners;
      const open = this.banners.map((b, i) => ({ b, at: field[i]! })).filter((x) => x.b.holder !== f.side);
      const pool = open.length ? open : this.banners.map((b, i) => ({ b, at: field[i]! }));
      goal = pool.reduce((a, c) => (dist2(f, c.at) < dist2(f, a.at) ? c : a)).at;
      stop = GUILD_WAR.captureRadius * 0.5;
    }
    const d = Math.sqrt(dist2(f, goal));
    if (d <= stop) return;
    const step = Math.min(d - stop, GUILD_WAR.runSpeed * dt);
    f.x += ((goal.x - f.x) / d) * step;
    f.y += ((goal.y - f.y) / d) * step;
  }

  /**
   * Foes in reach fight: each pair once a step, a defeat by chance, the stronger likelier to win, the winner worn.
   * Driven fighters (players, and foes fighting a player by hand) are left to their own fight.
   */
  private duels(dt: number, out: WarHappening[]): void {
    const busy = new Set<number>();
    this.fighters.forEach((f, i) => {
      if (!f.alive || f.driven || busy.has(i)) return;
      let j = -1;
      let bd = GUILD_WAR.fightRange ** 2;
      this.fighters.forEach((o, k) => {
        if (k === i || !o.alive || o.driven || o.side === f.side || busy.has(k)) return;
        const d = dist2(f, o);
        if (d <= bd) {
          bd = d;
          j = k;
        }
      });
      if (j < 0) return;
      busy.add(i).add(j);
      if (this.random() >= GUILD_WAR.duelRate * dt) return;
      const o = this.fighters[j]!;
      const fWins = this.random() < f.power / (f.power + o.power);
      this.defeat(fWins ? i : j, fWins ? j : i, out);
      const w = fWins ? f : o;
      w.hp = Math.max(0.05, w.hp - GUILD_WAR.wear);
    });
  }

  private capture(dt: number, out: WarHappening[]): void {
    const r2 = GUILD_WAR.captureRadius ** 2;
    this.banners.forEach((b, i) => {
      const at = GUILD_FIELD.banners[i]!;
      const present = new Set<number>();
      for (const f of this.fighters) if (f.alive && dist2(f, at) <= r2) present.add(f.side);
      const only = present.size === 1 ? [...present][0]! : null;
      if (only === null || only === b.holder) {
        b.capSide = null;
        b.capT = 0;
        return;
      }
      if (b.capSide === only) b.capT += dt;
      else {
        b.capSide = only;
        b.capT = dt;
      }
      if (b.capT >= GUILD_WAR.captureTime) {
        b.holder = only;
        b.capSide = null;
        b.capT = 0;
        out.push({ kind: 'capture', banner: b.id, side: only });
      }
    });
  }
}

// ───────────────────────── the system ─────────────────────────

export type GuildWarCommand =
  | { t: 'guildWarSignup'; mode: GuildWarMode }
  /** The client's local clock (minutes of the day). A server sets its own clock and must refuse this from players. */
  | { t: 'guildWarClock'; minute: number }
  /** Into the battlefield (answering the war's `prompt`), or out of it before the end. */
  | { t: 'guildWarEnter' }
  | { t: 'guildWarLeave' };

export type GuildWarEvent =
  | { type: 'guildWar'; what: 'reminder' | 'signup' | 'fallback' | 'prompt' | 'enter' | 'leave' }
  /** The battlefield's state, a few times a second while the player is inside (systems/guildField.ts). */
  | { type: 'guildWar'; what: 'field'; state: GuildFieldState }
  | { type: 'guildWar'; what: 'start'; mode: GuildWarMode; sides: string[] }
  | { type: 'guildWar'; what: 'capture'; banner: string; side: number }
  | { type: 'guildWar'; what: 'defeat'; killer: string; victim: string }
  | { type: 'guildWar'; what: 'end'; winner: number; points: number[]; won: boolean; gold: number; cultivation: number };

/**
 * Opponents for a `pvp` war: other players' guilds signed up on the server. The server sets this; in the
 * single-player build it is unset (or returns null) and the war falls back to `npc`.
 */
export type PvpMatcher = (mine: GuildState) => WarSideDef[] | null;

/** Crossing `mark` going from `a` to `b` (minutes of the day, wrapping at midnight). */
export const crossed = (a: number, b: number, mark: number): boolean =>
  b >= a ? a < mark && mark <= b : mark > a || mark <= b;

export class GuildWarSystem implements WorldSystem {
  readonly name = 'guildWar';
  /** The war in progress, if the player's guild fights tonight. */
  match: GuildWarMatch | null = null;
  /** Mode of the war in progress. */
  mode: GuildWarMode = 'npc';
  /** The server's matchmaker for `pvp` (null in single player). */
  pvpMatcher: PvpMatcher | null = null;
  /** The local (or server) clock in minutes of the day; null: the game's hour. */
  private clock: number | null = null;
  private last: number | null = null;
  /** The battlefield played by hand (systems/guildField.ts). */
  readonly field: GuildField;

  constructor(private readonly h: SystemHost) {
    this.field = new GuildField(
      h,
      () => this.match?.sides.map((s) => s.color ?? '#888888') ?? [],
      () => ((((GUILD_WAR.end - this.minute()) % 1440) + 1440) % 1440) * 60,
      () => this.mode === 'pvp',
    );
  }

  /** Minutes of the day by the war's clock. */
  minute(): number {
    return this.clock ?? this.h.world.hour * 60;
  }

  phase(): GuildWarPhase {
    return guildWarPhase(this.minute());
  }

  readonly commands = {
    guildWarSignup: (c: { mode: GuildWarMode }) => {
      const g = this.h.world.progress.guild;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (!isGuildWarMode(c.mode)) return;
      if (this.phase() !== 'signup') return this.h.notice('ปิดรับสมัครศึกกิลด์แล้ว');
      g.warSignup = c.mode;
      this.h.emit({ type: 'guildWar', what: 'signup' });
      this.h.emit({ type: 'progressChanged' });
      this.h.notice(`สมัครศึกกิลด์แล้ว (${GUILD_WAR_MODES[c.mode].label})`);
    },
    guildWarClock: (c: { minute: number }) => {
      const m = Number(c.minute);
      if (Number.isFinite(m)) this.clock = ((m % 1440) + 1440) % 1440;
    },
    guildWarEnter: () => {
      if (!this.match) return this.h.notice('ไม่มีศึกกิลด์ในขณะนี้');
      if (!this.field.available()) return this.h.notice('เข้าสมรภูมิจากที่นี่ไม่ได้');
      this.field.enter(this.match);
    },
    guildWarLeave: () => this.field.leave(this.match),
  };

  step(dt: number): void {
    if (this.clock !== null) this.clock = (this.clock + dt / 60) % 1440;
    const now = this.minute();
    const prev = this.last;
    this.last = now;
    if (prev === null) return;
    const g = this.h.world.progress.guild;
    if (g && crossed(prev, now, GUILD_WAR.reminder)) {
      this.h.emit({ type: 'guildWar', what: 'reminder' });
      this.h.notice(g.warSignup ? 'ศึกกิลด์จะเริ่มเวลา 20:00' : 'ศึกกิลด์จะเริ่มเวลา 20:00 ปิดรับสมัคร 19:50');
    }
    if (crossed(prev, now, GUILD_WAR.start)) this.begin();
    if (this.match) {
      this.field.before(this.match);
      for (const e of this.match.tick(dt)) this.happened(e);
      this.field.after(this.match, dt);
      if (crossed(prev, now, GUILD_WAR.end)) this.finish();
    }
  }

  /** The war starts: the player's guild against tonight's opponents, if it signed up. */
  private begin(): void {
    const g = this.h.world.progress.guild;
    if (!g) return;
    g.warTitle = false;
    const mode = g.warSignup;
    delete g.warSignup;
    if (!mode) return;
    let opponents = mode === 'pvp' ? (this.pvpMatcher?.(g) ?? null) : null;
    this.mode = opponents?.length ? 'pvp' : 'npc';
    if (mode === 'pvp' && this.mode === 'npc') {
      this.h.emit({ type: 'guildWar', what: 'fallback' });
      this.h.notice('ไม่มีกิลด์ผู้เล่นสมัคร ศึกคืนนี้สู้กับกิลด์ NPC');
    }
    if (!opponents?.length) opponents = this.aiSides(g);
    this.match = new GuildWarMatch([this.mySide(g), ...opponents.slice(0, 2)], () => this.h.random());
    this.h.emit({ type: 'guildWar', what: 'start', mode: this.mode, sides: this.match.sides.map((s) => s.name) });
    this.h.notice(`ศึกกิลด์เริ่มแล้ว ณ ${GUILD_FIELD.name}`);
    if (this.field.available()) this.h.emit({ type: 'guildWar', what: 'prompt' });
  }

  private mySide(g: GuildState): WarSideDef {
    const pr = this.h.world.progress;
    const me: WarFighterDef = { name: pr.name, power: 20 + rankFor(pr.cultivationSpent).index * 6, player: true };
    const rest = [...g.members].sort((a, b) => b.power - a.power).slice(0, GUILD_WAR.maxPerSide - 1);
    return { guildId: g.id, name: g.name, human: true, color: g.color, fighters: [me, ...rest.map((m) => ({ name: m.name, power: m.power }))] };
  }

  /** 1–2 simulated guilds, drawn from those the player is not in. */
  private aiSides(g: GuildState): WarSideDef[] {
    const pool = SIM_GUILDS.filter((s) => s.id !== g.id);
    const [lo, hi] = GUILD_WAR.aiGuilds;
    const n = Math.min(pool.length, lo + Math.floor(this.h.random() * (hi - lo + 1)));
    const out: WarSideDef[] = [];
    const left = [...pool];
    for (let k = 0; k < n; k++) {
      const s = left.splice(Math.floor(this.h.random() * left.length), 1)[0]!;
      out.push({ guildId: s.id, name: s.name, human: false, color: s.color, fighters: s.members.map((m) => ({ name: m.name, power: m.power })) });
    }
    return out;
  }

  private happened(e: WarHappening): void {
    const m = this.match!;
    if (e.kind === 'capture') {
      this.h.emit({ type: 'guildWar', what: 'capture', banner: e.banner, side: e.side });
      const b = GUILD_FIELD.banners.find((x) => x.id === e.banner)!;
      this.h.log('sys', `${m.sides[e.side]!.name} ยึด${b.name}`);
    } else {
      const k = m.fighters[e.killer]!;
      const v = m.fighters[e.victim]!;
      this.h.emit({ type: 'guildWar', what: 'defeat', killer: k.name, victim: v.name });
    }
  }

  /** The war ends: the winner's coffer, contribution and title; every fighter's ตำลึง and แต้มฝึกปรือ. */
  private finish(): void {
    const m = this.match!;
    this.field.leave(m);
    this.match = null;
    const r = m.result();
    const pr = this.h.world.progress;
    const g = pr.guild;
    const won = r.winner === 0;
    const R = GUILD_WAR_REWARDS;
    const bonus = 1 + (g ? guildLevel(g).warBonus : 0);
    const gold = Math.round((R.fighter.gold + (won ? R.winnerFighter.gold : 0)) * bonus);
    const cultivation = Math.round((R.fighter.cultivation + (won ? R.winnerFighter.cultivation : 0)) * bonus);
    pr.gold += gold;
    pr.cultivation += cultivation;
    let up: number | null = null;
    if (won && g) {
      g.coffer += R.winner.coffer;
      up = addGuildContribution(g, R.winner.contribution);
      g.warTitle = true;
    }
    this.h.emit({ type: 'guildWar', what: 'end', winner: r.winner, points: r.points, won, gold, cultivation });
    this.h.emit({ type: 'progressChanged' });
    this.h.notice(
      `${won ? `กิลด์ชนะศึก ได้ฉายา ${R.title}` : `${m.sides[r.winner]!.name} ชนะศึก`} · ได้ ${gold} ตำลึง แต้มฝึกปรือ ${cultivation}`,
    );
    if (up !== null) {
      this.h.emit({ type: 'guild', what: 'level', level: up });
      this.h.notice(`กิลด์ขึ้นระดับ ${up}`);
    }
  }
}
